/**
 * 挖掘脚本：从范例目录自动提取字段绑定知识库（XML→IR→绑定知识）。
 *
 * 用途：
 *   1. 扫描范例目录（bindings.examplesDir，缺省 src/examples/sample-center）
 *   2. 对每个 .xml 范例调用 parseReport() → IR → mineFromIr() 提取 (label, field, zone, side)
 *   3. 兼容 .json（内嵌 bindings/fields）与 .xlsx 套打模板（extractTemplateFromBuffer）
 *   4. 聚合为 BindingDict，可选写出到 bindings.dictPath
 *
 * 说明：内置静态知识库 src/data/binding-dict.json 已废弃，知识库完全来自动态提取。
 *
 * 知识库结构（BindingDict）：
 *   - labels: { [标签名]: { fields, confidence, count, sources } }
 *   - positions: { [zone_side]: { fields, confidence, count, sources } }
 *   - patterns: { [label@zone_side]: { fields, confidence, count, sources } }
 */

import * as fs from 'node:fs';
import * as path from 'node:path';
import { fileURLToPath } from 'node:url';
import {
  aggregateBindings,
  emptyBindingDict,
  mineFromIr,
  type BindingDict,
  type BindingKnowledge,
} from '../core/template/mineFromIr.js';
import { parseReport } from '../core/xml/parse.js';
import { getLogger } from '../core/logger.js';

const log = getLogger('scripts:mine-bindings');

/**
 * 递归扫描目录，找出 *.xml / *.json / *.xlsx 文件
 */
function scanExamples(dir: string): string[] {
  const files: string[] = [];

  function walk(current: string) {
    let entries: fs.Dirent[] = [];
    try {
      entries = fs.readdirSync(current, { withFileTypes: true });
    } catch (error) {
      log.warn(`⚠️ 扫描目录失败 (${current}): ${(error as Error).message}`);
      return;
    }
    for (const entry of entries) {
      const fullPath = path.join(current, entry.name);
      if (entry.isDirectory()) {
        walk(fullPath);
      } else if (
        entry.isFile() &&
        (entry.name.endsWith('.xml') ||
          entry.name.endsWith('.json') ||
          entry.name.endsWith('.xlsx'))
      ) {
        files.push(fullPath);
      }
    }
  }

  walk(dir);
  return files;
}

/**
 * 从单个 XML 范例提取绑定知识（parseReport → IR → mineFromIr）。
 */
function extractBindingsFromXml(
  filePath: string,
  xmlContent: string,
  maxRows: number = 100,
): BindingKnowledge[] {
  try {
    const ir = parseReport(xmlContent);
    if (!ir) return [];
    return mineFromIr(ir, path.basename(filePath), maxRows);
  } catch (error) {
    log.warn(`⚠️ 解析 XML 失败 (${path.basename(filePath)}): ${(error as Error).message}`);
  }
  return [];
}

/**
 * 从单个 JSON 范例提取绑定知识（兼容旧链路：内嵌 bindings/fields）。
 */
function extractBindingsFromJson(
  filePath: string,
  jsonContent: string,
): BindingKnowledge[] {
  try {
    const exampleData = JSON.parse(jsonContent);
    const exampleBindings: unknown[] =
      exampleData?.bindings || exampleData?.fields || [];
    if (!Array.isArray(exampleBindings)) return [];

    const bindings: BindingKnowledge[] = [];
    for (const field of exampleBindings) {
      const fieldName =
        typeof field === 'string'
          ? field
          : (field as Record<string, unknown>)?.name ||
            (field as Record<string, unknown>)?.fieldName;
      if (!fieldName) continue;
      bindings.push({
        label: (field as Record<string, unknown>)?.label as string | undefined,
        field: String(fieldName),
        zone: (field as Record<string, unknown>)?.zone as string | undefined,
        side: (field as Record<string, unknown>)?.side as string | undefined,
        confidence: 0.7,
        source: path.basename(filePath),
      });
    }
    return bindings;
  } catch (error) {
    log.warn(`⚠️ 解析 JSON 失败 (${path.basename(filePath)}): ${(error as Error).message}`);
  }
  return [];
}

/**
 * 主函数：运行挖掘脚本。
 *
 * @param examplesDir 范例/示例目录
 * @param outputPath 知识库输出路径（缺省/空字符串时不写盘）
 * @param maxRows 大模板只取前 N 行
 */
export async function mineBindingDict(
  examplesDir: string,
  outputPath?: string,
  maxRows: number = 100,
): Promise<BindingDict> {
  log.info(`⛏ 开始挖掘绑定知识库，范例目录：${examplesDir}`);

  if (!fs.existsSync(examplesDir)) {
    log.warn(`⚠️ 目录不存在：${examplesDir}`);
    return emptyBindingDict();
  }

  const exampleFiles = scanExamples(examplesDir);
  log.info(`📄 找到 ${exampleFiles.length} 个范例文件`);

  const allBindings: BindingKnowledge[] = [];

  for (const file of exampleFiles) {
    try {
      let bindings: BindingKnowledge[] = [];
      if (file.endsWith('.xml')) {
        bindings = extractBindingsFromXml(file, fs.readFileSync(file, 'utf-8'), maxRows);
      } else if (file.endsWith('.json')) {
        bindings = extractBindingsFromJson(file, fs.readFileSync(file, 'utf-8'));
      } else {
        // .xlsx 套打模板暂跳过（需要 xlsx 解析；见 extractTemplateFromBuffer）
        continue;
      }
      allBindings.push(...bindings);
      log.info(`✓ ${path.basename(file)}: ${bindings.length} 个绑定`);
    } catch (error) {
      log.warn(`✗ ${path.basename(file)}: ${(error as Error).message}`);
    }
  }

  log.info(`📊 总计提取 ${allBindings.length} 个绑定`);

  const bindingDict = aggregateBindings(allBindings);

  if (outputPath) {
    fs.mkdirSync(path.dirname(outputPath), { recursive: true });
    fs.writeFileSync(outputPath, JSON.stringify(bindingDict, null, 2));
    log.info(`✓ 知识库已保存到 ${outputPath}`);
  }

  log.info(`  - ${Object.keys(bindingDict.labels).length} 个标签`);
  log.info(`  - ${Object.keys(bindingDict.positions).length} 个位置`);
  log.info(`  - ${Object.keys(bindingDict.patterns).length} 个模式`);

  return bindingDict;
}

// CLI 入口：判断本模块文件本身是否就是 node 的入口脚本（避免被 import 时误执行）。
const currentFile = fileURLToPath(import.meta.url);
const invokedFile = process.argv[1] ? path.resolve(process.argv[1]) : '';
if (invokedFile && currentFile === invokedFile) {
  const examplesDir =
    process.argv[2] || path.join(process.cwd(), 'src/examples/sample-center');
  const outputPath = process.argv[3] || '';

  mineBindingDict(examplesDir, outputPath).catch((error) => {
    log.error('挖掘失败', error);
    process.exit(1);
  });
}
